// src/components/layout/Navbar.js
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLang, LANGUAGES } from '../../i18n/LanguageContext';
import { useSiteConfig } from '../../context/SiteConfigContext';
import styles from './Navbar.module.css';

const NAV_ITEMS = [
  { key: 'home',          fa: 'خانه',        en: 'Home',         path: '/' },
  { key: 'portfolio',     fa: 'نمونه‌کارها',  en: 'Portfolio',    path: '/portfolio' },
  { key: 'testimonials',  fa: 'رضایت‌مندی',  en: 'Testimonials', path: '/testimonials' },
  { key: 'blog',          fa: 'مقالات',       en: 'Blog',         path: '/blog' },
  { key: 'contact',       fa: 'تماس',         en: 'Contact',      path: '/#contact' },
];

export default function Navbar() {
  const { lang, switchLang, t, ui } = useLang();
  const { config } = useSiteConfig();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const brandName = t(config, 'brand_name');
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path.replace('/#', '/'));
  };

  return (
    <nav className={`${styles.nav} ${scrolled ? styles.scrolled : ''}`}
      style={{ fontFamily: lang === 'fa' ? 'var(--font-fa)' : 'var(--font-en)' }}>

      <div className={styles.inner}>
        {/* Logo */}
        <Link to="/" className={styles.logo}>
          <span className={styles.logoIcon}>🕯️</span>
          <span className={styles.logoText}>{brandName}</span>
        </Link>

        {/* Desktop Nav */}
        <div className={styles.links}>
          {NAV_ITEMS.map(({ key, fa, en, path }) => (
            <Link
              key={key}
              to={path}
              className={`${styles.link} ${isActive(path) ? styles.active : ''}`}
            >
              {ui(fa, en)}
            </Link>
          ))}
        </div>

        {/* Right controls */}
        <div className={styles.controls}>
          {/* Language toggle */}
          <div className={styles.langToggle}>
            {Object.entries(LANGUAGES).map(([code, info]) => (
              <button
                key={code}
                onClick={() => switchLang(code)}
                className={`${styles.langBtn} ${lang === code ? styles.langActive : ''}`}
              >
                {info.name}
              </button>
            ))}
          </div>

          {/* Contact CTA */}
          <Link
            to="/portfolio"
            className={styles.cta}
          >
            {ui('سفارش دهید', 'Order Now')}
          </Link>

          {/* Hamburger */}
          <button
            className={styles.hamburger}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            <span className={`${styles.bar} ${menuOpen ? styles.open1 : ''}`} />
            <span className={`${styles.bar} ${menuOpen ? styles.open2 : ''}`} />
            <span className={`${styles.bar} ${menuOpen ? styles.open3 : ''}`} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className={styles.drawer}>
          {NAV_ITEMS.map(({ key, fa, en, path }) => (
            <Link
              key={key}
              to={path}
              className={`${styles.drawerLink} ${isActive(path) ? styles.active : ''}`}
            >
              {ui(fa, en)}
            </Link>
          ))}
          <div className={styles.drawerLang}>
            {Object.entries(LANGUAGES).map(([code, info]) => (
              <button key={code} onClick={() => switchLang(code)}
                className={`${styles.langBtn} ${lang === code ? styles.langActive : ''}`}>
                {info.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
